// src/pages/admin/manager/UsuariosPage.tsx
import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Stack,
  Button,
  Paper,
  IconButton,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import StarIcon from '@mui/icons-material/Star';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import UserDialog, { UserForm } from '../../../components/UserDialog';
import GenericExportDialog from '../../../components/GenericExportDialog';
import AsignarFormadorDialog from '../../../components/AsignarFormadorDialog';
import { useUsuarios, Usuario } from '../../../hooks/useUsuarios';
import api from '../../../api/axios';
import { addManager, removeManager, getRolesForUsuario, DepartamentoRol } from '../../../api/departamentoManagers';
import { useDepartamentoManagers } from '../../../hooks/useDepartamentoManagers';
import { useAuth } from '../../../context/AuthContext';

/* ——— helpers de rol ——— */
const isProfesor = (u: Usuario) => u.rol === 'profesor';
const isCliente = (u: Usuario) => u.rol === 'cliente';
const isStaff = (u: Usuario) => !isProfesor(u) && !isCliente(u);

/* ——— tipos para pestañas ——— */
type TabKey = 'staff' | 'profesores' | 'clientes';

/* ——— normalizar 'activo' a boolean ——— */
const isActiveTrue = (v: any): boolean => {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (['1', 'true', 'si', 'sí', 'activo', 'active', 'enabled', 'yes'].includes(s)) return true;
    if (['0', 'false', 'no', 'inactivo', 'inactive', 'baja', 'archivado', 'disabled'].includes(s))
      return false;
  }
  return !!v;
};

/* ——— util fecha: a YYYY-MM-DD para <input type="date" /> ——— */
const toYMD = (v: any): string => {
  if (!v) return '';
  if (typeof v === 'string') {
    const m = v.match(/^\d{4}-\d{2}-\d{2}/);
    if (m) return m[0];
  }
  try {
    const d = new Date(v);
    if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  } catch {}
  return '';
};

export default function UsuariosPage() {
  const { t } = useTranslation<'translation'>();
  const navigate = useNavigate();

  /* -------- datos backend (hook actualizado) -------- */
  const { rows, loading, refresh } = useUsuarios();

  /* -------- cargar managers -------- */
  // Cargar managers si el usuario es admin o hr (ambos tienen permisos)
  const { user } = useAuth();
  const isAdmin = user?.roles?.includes('admin');
  const isHr = user?.roles?.includes('hr');
  const puedeVerManagers = isAdmin || isHr;
  // Solo cargar managers si tiene permisos (admin o hr)
  const managersHook = useDepartamentoManagers();
  const managers = puedeVerManagers ? managersHook.managers : [];
  const loadingManagers = puedeVerManagers ? managersHook.loading : false;
  const refreshManagers = puedeVerManagers ? managersHook.refresh : async () => {};
  
  // Crear un Set de IDs de usuarios que son managers para búsqueda rápida
  const managersSet = useMemo(() => {
    const set = new Set<number>();
    managers.forEach((m) => {
      set.add(m.usuarioId);
    });
    return set;
  }, [managers]);

  // Función helper para verificar si un usuario es manager de su departamento
  const esManagerDeSuDepartamento = useCallback((usuario: Usuario): boolean => {
    const staffRoles: DepartamentoRol[] = [
      'facturacion',
      'att_familias',
      'att_alumnado',
      'att_profesorado',
      'admisiones',
      'hr',
    ];
    
    if (!staffRoles.includes(usuario.rol as DepartamentoRol)) {
      return false;
    }
    
    // Verificar si es manager de su propio rol
    return managersSet.has(usuario.id) && 
           managers.some(m => m.usuarioId === usuario.id && m.rol === usuario.rol);
  }, [managers, managersSet]);

  /* -------- estado UI -------- */
  const [editing, setEditing] = useState<Usuario>();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<TabKey>('staff');
  const [showInactive, setShowInactive] = useState(false);
  const [token, setToken] = useState<string>('');
  const [modalFormadorOpen, setModalFormadorOpen] = useState(false);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState<Usuario | null>(null);

  /* carga inicial */
  useEffect(() => {
    refresh();
    setToken('mi_token_de_autorizacion');
  }, [refresh]);

  /* -------- CRUD wrappers locales -------- */
  const createUser = useCallback(
    async (dto: any) => {
      await api.post('/usuarios', dto);
      await refresh();
    },
    [refresh],
  );

  const updateUser = useCallback(
    async (id: number, dto: any) => {
      await api.put(`/usuarios/${id}`, dto);
      await refresh();
    },
    [refresh],
  );

  const removeUser = useCallback(
    async (id: number) => {
      await api.delete(`/usuarios/${id}`);
      await refresh();
    },
    [refresh],
  );

  const openNew = () => {
    setEditing(undefined);
    setDialogOpen(true);
  };

  /* ⬇️ MODIFICADO: cargar detalle para tener fecha_incorporacion y estado de manager */
  const openEdit = async (u: Usuario) => {
    try {
      const { data } = await api.get(`/usuarios/${u.id}`);
      const fecha = toYMD((data as any).fechaIncorporacion ?? (data as any).fecha_incorporacion);

      // Verificar si es manager de su departamento
      const staffRoles: DepartamentoRol[] = [
        'facturacion',
        'att_familias',
        'att_alumnado',
        'att_profesorado',
        'admisiones',
        'hr',
      ];
      let esManager = false;
      if (u.rol && staffRoles.includes(u.rol as DepartamentoRol)) {
        try {
          const rolesManager = await getRolesForUsuario(u.id);
          esManager = rolesManager.includes(u.rol as DepartamentoRol);
        } catch (err) {
          // Error verificando managers
        }
      }

      // mergeamos lo que ya teníamos con lo que venga del detalle
      setEditing({
        ...u,
        ...data,
        fechaIncorporacion:
          fecha || (u as any).fechaIncorporacion || (u as any).fecha_incorporacion,
        esManager,
      } as Usuario & { esManager?: boolean });
    } catch (err) {
      // fallback: abrimos con lo que tengamos
      setEditing(u);
    } finally {
      setDialogOpen(true);
    }
  };

  const openAsignarFormador = (usuario: Usuario) => {
    setUsuarioSeleccionado(usuario);
    setModalFormadorOpen(true);
  };

  /* -------- filtro combinado -------- */
  const filtered = useMemo(() => {
    const byTab = rows.filter(
      tab === 'staff' ? isStaff : tab === 'profesores' ? isProfesor : isCliente,
    );

    const byActive = byTab.filter((u) =>
      showInactive ? !isActiveTrue(u.activo) : isActiveTrue(u.activo),
    );

    if (!search.trim()) return byActive;

    const q = search.toLowerCase();
    return byActive.filter(
      (u) =>
        u.email.toLowerCase().includes(q) || (u.nombreCompleto ?? '').toLowerCase().includes(q),
    );
  }, [rows, tab, showInactive, search]);

  /* -------- contadores -------- */
  const counters = useMemo(() => {
    const byTab = rows.filter(
      tab === 'staff' ? isStaff : tab === 'profesores' ? isProfesor : isCliente,
    );
    const activos = byTab.filter((u) => isActiveTrue(u.activo)).length;
    const inactivos = byTab.length - activos;
    return {
      total: byTab.length,
      activos,
      inactivos,
      staffRoles:
        tab === 'staff'
          ? Array.from(
              byTab.reduce((acc, u) => {
                const rol = u.rol || 'sin-rol';
                acc.set(rol, (acc.get(rol) ?? 0) + 1);
                return acc;
              }, new Map<string, number>()),
            ).sort((a, b) => a[0].localeCompare(b[0]))
          : [],
    };
  }, [rows, tab]);

  /* -------- alta / edición -------- */
  const handleSave = useCallback(
    async (form: UserForm, id?: number) => {
      const fullName = `${form.nombre ?? ''} ${form.apellidos ?? ''}`.trim() || undefined;

      const payload: Record<string, unknown> = {
        email: form.email,
        rol: form.rol,
        activo: !!form.activo,
        ...(fullName ? { nombreCompleto: fullName } : {}),
      };

      // Incluir fechaIncorporacion si el rol no es cliente
      // Si estamos editando y no viene fechaIncorporacion en el form pero el usuario tiene una,
      // el backend la preservará automáticamente
      if (form.rol !== 'cliente') {
        if (form.fechaIncorporacion) {
          payload.fechaIncorporacion = form.fechaIncorporacion;
        } else if (editing && (editing as any).fechaIncorporacion) {
          // Preservar fecha existente si no se envió una nueva
          payload.fechaIncorporacion = toYMD(
            (editing as any).fechaIncorporacion ?? (editing as any).fecha_incorporacion,
          );
        }
      }

      // Roles del staff que pueden ser managers (excluyendo admin)
      const staffRoles: DepartamentoRol[] = [
        'facturacion',
        'att_familias',
        'att_alumnado',
        'att_profesorado',
        'admisiones',
        'hr',
      ];
      const puedeSerManager = form.rol && staffRoles.includes(form.rol as DepartamentoRol);
      const esManagerDeseado = form.esManager === true;
      const rolComoManager = puedeSerManager ? (form.rol as DepartamentoRol) : null;

      try {
        let usuarioIdFinal = id;

        // Crear o actualizar usuario
        if (id) {
          await updateUser(id, payload);
          usuarioIdFinal = id;
        } else {
          // Al crear, necesitamos obtener el ID del usuario creado
          const response = await api.post('/usuarios', { ...payload, password: form.password });
          usuarioIdFinal = response.data?.id;
        }

        // Manejar manager solo si el rol puede ser manager
        if (puedeSerManager && rolComoManager && usuarioIdFinal) {
          // Verificar si el usuario era manager antes (solo al editar)
          const eraManager = editing && (editing as any)?.esManager;

          if (esManagerDeseado && !eraManager) {
            // Agregar como manager
            try {
              await addManager(usuarioIdFinal, rolComoManager);
              await refreshManagers(); // Refrescar lista de managers inmediatamente
            } catch (err: any) {
              // No fallar todo si solo falla el manager
            }
          } else if (!esManagerDeseado && eraManager) {
            // Remover como manager
            try {
              await removeManager(usuarioIdFinal, rolComoManager);
              await refreshManagers(); // Refrescar lista de managers inmediatamente
            } catch (err: any) {
              // No fallar todo si solo falla el manager
            }
          }
        } else if (usuarioIdFinal && editing && (editing as any)?.esManager && !puedeSerManager) {
          // Si cambió de rol y ya no puede ser manager, removerlo
          const rolAnterior = editing.rol;
          if (rolAnterior && staffRoles.includes(rolAnterior as DepartamentoRol)) {
            try {
              await removeManager(usuarioIdFinal, rolAnterior as DepartamentoRol);
              await refreshManagers(); // Refrescar lista de managers inmediatamente
            } catch (err: any) {
              // Error removiendo manager al cambiar rol
            }
          }
        }

        setDialogOpen(false);
        // Refrescar ambas listas para que la estrella aparezca/desaparezca inmediatamente
        await Promise.all([refresh(), refreshManagers()]);
      } catch (err) {
        // Error guardando usuario
      }
    },
    [createUser, updateUser, editing, refresh, refreshManagers],
  );

  /* -------- configuración Export -------- */
  const exportFields = [
    { key: 'id', label: 'ID' },
    { key: 'email', label: 'E-mail' },
    { key: 'nombreCompleto', label: 'Nombre completo' },
    { key: 'rol', label: 'Rol' },
    { key: 'activo', label: 'Activo' },
    { key: 'mustChangePwd', label: 'Must change pwd' },
    { key: 'tokenVersion', label: 'Token version' },
    { key: 'emailPadre', label: 'E-mail padre' },
  ];

  const exportFilters = [
    { key: 'admin', label: 'Admin' },
    { key: 'att_familias', label: 'Att. Familias' },
    { key: 'att_profesorado', label: 'Att. Profesorado' },
    { key: 'att_alumnado', label: 'Att. Alumnado' },
    { key: 'facturacion', label: 'Facturación' },
    { key: 'hr', label: 'HR' },
    { key: 'admisiones', label: 'Admisiones' },
    { key: 'consultor', label: 'Consultor' },
    { key: 'profesor', label: 'Profesor' },
    { key: 'cliente', label: 'Cliente' },
  ];

  return (
    <Stack spacing={2}>
      {/* contadores arriba */}
      <Paper sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
        <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
          <Stack direction="row" spacing={1} alignItems="center">
            <span style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>{counters.total}</span>
            <span>usuarios</span>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <span style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>{counters.activos}</span>
            <span>activos</span>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <span style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>{counters.inactivos}</span>
            <span>inactivos</span>
          </Stack>
        </Stack>

        {tab === 'staff' && counters.staffRoles.length > 0 && (
          <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
            {counters.staffRoles.map(([rol, count]) => (
              <Chip
                key={rol}
                label={`${t(`roles.${rol}`, rol)}: ${count}`}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'inherit' }}
              />
            ))}
          </Stack>
        )}
      </Paper>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as TabKey)}
        aria-label="Tabs de rol de usuario"
      >
        <Tab value="staff" label={t('usuarios.tabs.staff')} />
        <Tab value="profesores" label={t('usuarios.tabs.profesores')} />
        <Tab value="clientes" label={t('usuarios.tabs.clientes')} />
      </Tabs>

      <Stack direction="row" spacing={2} alignItems="center">
        <Button startIcon={<AddIcon />} variant="contained" onClick={openNew}>
          {t('usuarios.actions.nuevo')}
        </Button>

        <Button
          startIcon={<FileDownloadIcon />}
          variant="outlined"
          onClick={() => setExportOpen(true)}
        >
          {t('usuarios.actions.exportar')}
        </Button>

        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t<string>('usuarios.placeholder.buscar')}
          size="small"
          sx={{ maxWidth: 320 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
          }
          label={t('usuarios.label.mostrarInactivos')}
        />
      </Stack>

      <Paper>
        {loading ? (
          <Stack alignItems="center" py={4}>
            <CircularProgress />
          </Stack>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('usuarios.table.id')}</TableCell>
                <TableCell>{t('usuarios.table.email')}</TableCell>
                <TableCell>{t('usuarios.table.nombre')}</TableCell>
                <TableCell>{t('usuarios.table.rol')}</TableCell>
                <TableCell>{t('usuarios.table.activo')}</TableCell>
                <TableCell align="center">{t('usuarios.table.acciones')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((u) => (
                <TableRow key={u.id}>
                  <TableCell>{u.id}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <span>{u.nombreCompleto || '—'}</span>
                      {esManagerDeSuDepartamento(u) && (
                        <StarIcon 
                          sx={{ 
                            fontSize: '1rem', 
                            color: 'warning.main',
                            verticalAlign: 'middle'
                          }} 
                          titleAccess="Manager del departamento"
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>{t(`roles.${u.rol}`, u.rol)}</TableCell>
                  <TableCell>
                    {isActiveTrue(u.activo) ? (
                      <Chip label={t('usuarios.si')} color="success" size="small" />
                    ) : (
                      <Chip label={t('usuarios.no')} color="default" size="small" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={t('usuarios.tooltip.editar')}>
                      <IconButton size="small" onClick={() => openEdit(u)}>
                        <EditIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>

                    {u.rol === 'profesor' && (
                      <Tooltip title={t('usuarios.tooltip.asignarFormador')}>
                        <IconButton size="small" onClick={() => openAsignarFormador(u)}>
                          <AssignmentTurnedInIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Ver evaluaciones">
                      <IconButton 
                        size="small" 
                        onClick={() => navigate(`/admin/manager/usuarios/${u.id}/evaluaciones`)}
                      >
                        <AssignmentIndIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <UserDialog
        key={editing ? `edit-${editing.id}` : 'new'}
        open={dialogOpen}
        initial={
          editing && {
            id: editing.id,
            email: editing.email,
            rol: editing.rol,
            activo: isActiveTrue(editing.activo),
            nombre: (editing.nombreCompleto ?? '').split(' ')[0] || '',
            apellidos: (editing.nombreCompleto ?? '').split(' ').slice(1).join(' '),
            esManager: (editing as any).esManager ?? false,
            ...(editing.rol !== 'cliente'
              ? {
                  // ⬇️ usar fecha de cualquier origen y en formato YYYY-MM-DD
                  fechaIncorporacion: toYMD(
                    (editing as any).fechaIncorporacion ?? (editing as any).fecha_incorporacion,
                  ),
                }
              : {}),
          }
        }
        onClose={() => setDialogOpen(false)}
        onSave={(form) => handleSave(form, editing?.id)}
        onDelete={
          editing
            ? async () => {
                await removeUser(editing.id);
                setDialogOpen(false);
              }
            : undefined
        }
      />

      <GenericExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        endpoint="/usuarios/export"
        entityName="usuarios"
        fields={exportFields}
        filters={exportFilters}
      />

      {usuarioSeleccionado && (
        <AsignarFormadorDialog
          open={modalFormadorOpen}
          onClose={() => setModalFormadorOpen(false)}
          userId={usuarioSeleccionado.id}
        />
      )}
    </Stack>
  );
}
